import { Head, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import FormInput from '@/Components/FormInput';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Edit({ material, flash }) {
    const { data, setData, put, processing, errors } = useForm({
        date: material.date ? material.date.split('T')[0] : '',
        supplier_name: material.supplier_name || '',
        notes: material.notes || '',
        reason: material.reason || '',
        items: material.items?.length
            ? material.items.map(i => ({
                item_name: i.item_name || '',
                quantity: i.quantity || 1,
                unit: i.unit || 'pcs',
                price: i.price || 0,
                notes: i.notes || '',
            }))
            : [{ item_name: '', quantity: 1, unit: 'pcs', price: 0, notes: '' }],
    });

    const addItem = () => setData('items', [...data.items, { item_name: '', quantity: 1, unit: 'pcs', price: 0, notes: '' }]);
    const removeItem = (i) => setData('items', data.items.filter((_, idx) => idx !== i));
    const updateItem = (i, field, val) => {
        const items = [...data.items];
        items[i][field] = val;
        setData('items', items);
    };

    const total = data.items.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.price)), 0);

    const submit = (e) => {
        e.preventDefault();
        put(`/materials/${material.id}`);
    };

    const statusBadge = (s) => {
        const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'Unknown'}</Badge>;
    };

    return (
        <AppLayout>
            <Head title="Edit Requisition" />
            <PageHeader
                title="Edit Requisition"
                subtitle={
                    <span className="flex items-center gap-2">
                        #{material.formatted_number || String(material.number).padStart(4, '0')}
                        {statusBadge(material.status)}
                    </span>
                }
                actions={
                    <Button href="/materials" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}

            {material.status === 'rejected' && material.rejected_reason && (
                <Alert type="danger" className="mb-4">
                    <strong>Rejected:</strong> {material.rejected_reason}
                </Alert>
            )}

            <form onSubmit={submit}>
                <Card className="mb-6">
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput
                                label="Date"
                                type="date"
                                value={data.date}
                                onChange={e => setData('date', e.target.value)}
                                error={errors.date}
                                required
                            />
                            <FormInput
                                label="Supplier Name"
                                value={data.supplier_name}
                                onChange={e => setData('supplier_name', e.target.value)}
                                error={errors.supplier_name}
                                placeholder="Enter supplier name"
                                required
                            />
                        </div>
                        <FormTextarea
                            label="Reason"
                            value={data.reason}
                            onChange={e => setData('reason', e.target.value)}
                            error={errors.reason}
                            rows={2}
                            placeholder="Reason for requisition"
                        />
                        <FormTextarea
                            label="Notes"
                            value={data.notes}
                            onChange={e => setData('notes', e.target.value)}
                            error={errors.notes}
                            rows={2}
                            placeholder="Additional notes (optional)"
                        />
                    </CardBody>
                </Card>

                <Card>
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="font-semibold text-slate-800">Line Items</h3>
                        <Button type="button" variant="outline" size="xs" onClick={addItem}>
                            <PlusIcon className="w-3 h-3" /> Add Item
                        </Button>
                    </div>
                    <CardBody>
                        <div className="space-y-4">
                            {data.items.map((item, i) => (
                                <div key={i} className="grid grid-cols-12 gap-3 items-end p-4 rounded-xl bg-slate-50 border border-slate-200">
                                    <div className="col-span-12 md:col-span-3">
                                        <FormInput
                                            label="Item Name"
                                            value={item.item_name}
                                            onChange={e => updateItem(i, 'item_name', e.target.value)}
                                            error={errors[`items.${i}.item_name`]}
                                            placeholder="Item description"
                                            required
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-2">
                                        <FormInput
                                            label="Qty"
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={item.quantity}
                                            onChange={e => updateItem(i, 'quantity', e.target.value)}
                                            error={errors[`items.${i}.quantity`]}
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-1">
                                        <FormInput
                                            label="Unit"
                                            value={item.unit}
                                            onChange={e => updateItem(i, 'unit', e.target.value)}
                                            placeholder="pcs"
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-2">
                                        <FormInput
                                            label="Price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={item.price}
                                            onChange={e => updateItem(i, 'price', e.target.value)}
                                            error={errors[`items.${i}.price`]}
                                        />
                                    </div>
                                    <div className="col-span-8 md:col-span-2">
                                        <FormInput
                                            label="Notes"
                                            value={item.notes}
                                            onChange={e => updateItem(i, 'notes', e.target.value)}
                                            placeholder="Item notes"
                                        />
                                    </div>
                                    <div className="col-span-4 md:col-span-2 flex items-center justify-between md:justify-end gap-2">
                                        <span className="text-sm font-medium text-slate-600">
                                            AED {(Number(item.quantity) * Number(item.price)).toFixed(2)}
                                        </span>
                                        {data.items.length > 1 && (
                                            <button type="button" onClick={() => removeItem(i)} className="p-2 hover:bg-red-50 rounded-lg">
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {errors.items && <p className="mt-2 text-xs text-red-500">{errors.items}</p>}
                        <div className="mt-4 text-right">
                            <span className="text-lg font-bold text-slate-800">Total: AED {total.toFixed(2)}</span>
                        </div>
                    </CardBody>
                </Card>

                <div className="flex gap-3 mt-6">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button href="/materials" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
